import type { MineralType } from 'types/minerals';
import type {
  DiFormat,
  QiFormat,
  ForecastDurationOption,
} from 'types/api-payloads';

export type DeclineModel =
  'exponential' | 'hyperbolic' | 'modified_hyperbolic' | 'ratio';

export type RampUpModel = 'none' | 'constant' | 'linear' | 'exponential';

export interface StreamDcaParams {
  dcaModel: DeclineModel;
  qi: number | null;
  di: number | null;
  bFactor: number | null;
  dlim: number | null;
  rampUpModel: RampUpModel;
  rampUpQi: number | null;
  rampUpDuration: number | null;
  rampUpN: number | null;
  ri: number | null;
  tElf: number | null;
  m: number | null;
}

export interface TcDcaFormValues {
  diFormat: DiFormat;
  qiFormat: QiFormat;
  forecastDurationPeriod: number;
  forecastDurationOption: ForecastDurationOption;
  oilDcaParams: StreamDcaParams;
  gasDcaParams: StreamDcaParams;
  waterDcaParams: StreamDcaParams;
}

/** Which parameters are locked (value fixed during regression). */
export interface StreamLockState {
  dca: Record<string, number>;
  rampUp: Record<string, number>;
}

export type AllLockStates = Record<MineralType, StreamLockState>;

// --- Regression API types ---

export interface TcDcaRegressionRequest {
  typeCurveDcaRegression: {
    uwis: string[];
    aggregation: string;
    dcaScenarioId: number;
    typeCurveHeaderDetailsId: number;
    dcaParams: {
      diFormat: DiFormat;
      forecastDurationOption: ForecastDurationOption;
      oilDcaParams?: StreamRegressionParams;
      gasDcaParams?: StreamRegressionParams;
      waterDcaParams?: StreamRegressionParams;
    };
  };
}

export interface StreamRegressionParams {
  dcaModel: DeclineModel;
  qiFormat: QiFormat;
  durationPeriod: number;
  durationFormat: 'years' | 'months';
  lockedParameters: Record<string, number>;
  unlockedParameters: string[];
  rampUp?: {
    model: 'constant' | 'linear' | 'exponential';
    lockedParameters: Record<string, number>;
    unlockedParameters: string[];
  };
}

export interface StreamRegressionResult {
  optimizedParameters: {
    dlim: number;
    qi: number;
    di: number;
    bFactor: number;
    model: string;
    rampUp: Record<string, unknown>;
  };
  segmentStartDates: string[];
  outlierDates: string[];
}

export interface TcDcaRegressionResponse {
  typeCurveDcaRegressionResult: {
    aggregation: string;
    status: 'success' | 'error';
    results: {
      oil?: StreamRegressionResult;
      gas?: StreamRegressionResult;
      water?: StreamRegressionResult;
    };
    errors: Array<{ message: string; code?: string }>;
  };
}

// --- Default values ---

export const DEFAULT_STREAM_DCA_PARAMS: StreamDcaParams = {
  dcaModel: 'modified_hyperbolic',
  qi: null,
  di: null,
  bFactor: null,
  dlim: null,
  rampUpModel: 'none',
  rampUpQi: null,
  rampUpDuration: null,
  rampUpN: null,
  ri: null,
  tElf: null,
  m: null,
};

export const DEFAULT_TC_DCA_FORM_VALUES: TcDcaFormValues = {
  diFormat: 'effective',
  qiFormat: 'months',
  forecastDurationPeriod: 30,
  forecastDurationOption: 'forecast_start',
  oilDcaParams: { ...DEFAULT_STREAM_DCA_PARAMS },
  gasDcaParams: { ...DEFAULT_STREAM_DCA_PARAMS },
  waterDcaParams: { ...DEFAULT_STREAM_DCA_PARAMS },
};

export const DEFAULT_STREAM_LOCK_STATE: StreamLockState = {
  dca: {},
  rampUp: {},
};

export const DEFAULT_ALL_LOCK_STATES: AllLockStates = {
  oil: { ...DEFAULT_STREAM_LOCK_STATE, dca: {}, rampUp: {} },
  gas: { ...DEFAULT_STREAM_LOCK_STATE, dca: {}, rampUp: {} },
  water: { ...DEFAULT_STREAM_LOCK_STATE, dca: {}, rampUp: {} },
};
